class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.scrollLock = {
      locked: false,
      scrollY: 0,
      previous: {},
    };

    this.preventBackgroundScroll = this.preventBackgroundScroll.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  lockPageScroll() {
    if (this.scrollLock.locked) return;

    const docEl = document.documentElement;
    const body = document.body;
    const scrollY = window.pageYOffset || docEl.scrollTop || 0;

    this.scrollLock = {
      locked: true,
      scrollY,
      previous: {
        htmlOverflow: docEl.style.overflow || '',
        htmlOverscroll: docEl.style.overscrollBehavior || '',
        bodyPosition: body.style.position || '',
        bodyTop: body.style.top || '',
        bodyLeft: body.style.left || '',
        bodyRight: body.style.right || '',
        bodyWidth: body.style.width || '',
        bodyOverflow: body.style.overflow || '',
        hadBodyOverflowHidden: body.classList.contains('overflow-hidden'),
      },
    };

    docEl.classList.add('dj-cart-is-open');
    body.classList.add('dj-cart-is-open', 'overflow-hidden');

    docEl.style.overflow = 'hidden';
    docEl.style.overscrollBehavior = 'none';

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    document.addEventListener('touchmove', this.preventBackgroundScroll, { passive: false });
    document.addEventListener('wheel', this.preventBackgroundScroll, { passive: false });
  }

  unlockPageScroll() {
    if (!this.scrollLock.locked) return;

    const docEl = document.documentElement;
    const body = document.body;
    const previous = this.scrollLock.previous || {};
    const scrollY = this.scrollLock.scrollY || 0;

    document.removeEventListener('touchmove', this.preventBackgroundScroll, { passive: false });
    document.removeEventListener('wheel', this.preventBackgroundScroll, { passive: false });

    docEl.classList.remove('dj-cart-is-open');
    body.classList.remove('dj-cart-is-open');
    if (!previous.hadBodyOverflowHidden) body.classList.remove('overflow-hidden');

    docEl.style.overflow = previous.htmlOverflow || '';
    docEl.style.overscrollBehavior = previous.htmlOverscroll || '';

    body.style.position = previous.bodyPosition || '';
    body.style.top = previous.bodyTop || '';
    body.style.left = previous.bodyLeft || '';
    body.style.right = previous.bodyRight || '';
    body.style.width = previous.bodyWidth || '';
    body.style.overflow = previous.bodyOverflow || '';

    window.scrollTo(0, scrollY);

    this.scrollLock.locked = false;
  }

  preventBackgroundScroll(event) {
    if (!this.classList.contains('active')) return;

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.contains(event.target)) return;

    event.preventDefault();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.lockPageScroll();

    // Trigger the CSS transition after the browser has applied the fixed viewport styles.
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockPageScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const replacementHTML = this.getSectionInnerHTML(parsedState.sections && parsedState.sections[section.id], section.selector);
      if (replacementHTML !== null) sectionElement.innerHTML = replacementHTML;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);